/**
 * Decision logic behind the Cash Management row actions and its error reporting.
 *
 * It lives outside the .vue so it can be exercised directly: the project has no
 * component-test tooling, and these are exactly the rules that went wrong in
 * production — a row offering an action the API will refuse, or an API refusal
 * the user never sees.
 */

/**
 * The reason ShiftController::clockIn() stamps on the opening-float row, and
 * the same constant CashTransaction::OPENING_CASH_REASON holds server-side.
 * The float is also carried on shifts.opening_cash and is excluded from
 * cash_in_total, so reversing the row would move one without the other.
 */
export const OPENING_CASH_REASON = "Opening cash";

/**
 * Whether a reversing entry may be posted against this row.
 *
 * Mirrors CashTransaction::isCancellable() plus the shift check
 * CashTransactionController::cancel() enforces: only a row on the caller's own
 * still-open shift can be corrected, because a closed shift has already been
 * counted and reconciled against its expected cash.
 *
 * This only decides whether to OFFER the button — the backend re-checks every
 * one of these rules, so a stale page cannot talk it into anything.
 */
export const isCancellable = (item, activeShift) => {
  if (!item) return false;
  if (item.is_cancelled || item.is_reversal) return false;
  if (item.reason === OPENING_CASH_REASON) return false;
  if (!activeShift) return false;
  if (item.shift_id === null || item.shift_id === undefined) return false;
  return Number(item.shift_id) === Number(activeShift.id);
};

/**
 * The action buttons a row gets. View is always available; cancel appears only
 * when isCancellable() says the API would accept it. Edit and delete are gone
 * for good — cash transactions are an audit trail.
 */
export const buildRowActions = ({ item, activeShift, t, onView, onCancel }) => {
  const actions = [
    {
      icon: "fal fa-eye",
      color: "blue",
      tooltip: t("description"),
      action: () => onView(item),
    },
  ];

  if (isCancellable(item, activeShift)) {
    actions.push({
      icon: "fal fa-undo",
      color: "orange",
      tooltip: t("cancel_transaction"),
      action: () => onCancel(item),
    });
  }

  return actions;
};

/**
 * Which i18n key to alert for a rejected request, or null to stay quiet.
 *
 * The axios interceptor rejects with `error.response.data` once it has ALREADY
 * alerted, and with the raw axios error when it has not — the 404 branch, which
 * passes straight through "for the component to handle". Only the raw error
 * still carries `.response`, so keying on that is what distinguishes "nobody
 * has told the user yet" from "do not alert twice".
 *
 * Getting this wrong is what made the cash-out button look dead on UAT: a
 * cash_out with no open shift aborts 404 active_shift_not_found, the
 * interceptor skipped it, and the component's catch only wrote to the console.
 */
export const apiErrorMessage = (err) => {
  if (!err || !err.response) return null;
  const message = err.response.data && err.response.data.message;
  return typeof message === "string" && message.length > 0
    ? message
    : "error_occurred";
};

export default { OPENING_CASH_REASON, isCancellable, buildRowActions, apiErrorMessage };
